<template>
    <div class="mt-2 flex min-h-[20px] flex-wrap items-center gap-5 lg:mb-2 lg:gap-2">
        <div
            v-if="canRenderBreadcrumbPrefix && breadcrumbPrefix && breadcrumbPrefixEnabled"
            class="group flex w-full items-center gap-2 sm:w-auto">
            <NuxtLink
                class="font-mori cursor-pointer pr-5 text-xs leading-[0] duration-300 hover:text-green-500 min-[351px]:text-sm lg:text-sm"
                @click="handleClick($event, getPath(breadcrumbPrefix.url))"
                :to="breadcrumbTarget(breadcrumbPrefix.url)">
                <NuxtImg
                    :alt="snippets['headless.breadcrumbs.arrowLeft']"
                    class="inline size-3 break-words"
                    width="12"
                    height="12"
                    src="/icons/arrow-left.svg" />
                {{ breadcrumbPrefix.name }}
            </NuxtLink>
        </div>

        <div class="group hidden items-center gap-2 md:flex" v-for="(param, index) in breadcrumbs" :key="index">
            <NuxtLink
                class="font-mori text-xs capitalize duration-300 hover:text-green-500 min-[351px]:text-sm lg:text-sm"
                :to="breadcrumbTarget(param.url)"
                v-if="index !== breadcrumbs.length - 1"
                >{{ param.name }}
            </NuxtLink>
            <span
                class="font-mori block text-xs tracking-[0.3px] capitalize min-[351px]:text-sm lg:text-sm lg:tracking-[0.7px]"
                v-if="index === breadcrumbs.length - 1"
                >{{ param.name }}
            </span>
            <div
                class="font-mori pl-2 text-xs text-gray-300 capitalize lg:pl-1 lg:text-sm"
                v-if="index < breadcrumbs.length - 1">
                /
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { NuxtLink } from '#components';
    import { categoryNavigationQuery, departmentFromPath } from '~/utils/plpNavigation';

    const props = withDefaults(
        defineProps<{
            breadcrumbPrefixEnabled?: boolean;
            retainFilters?: boolean;
        }>(),
        {
            breadcrumbPrefixEnabled: true,
        },
    );

    const snippetsStore = useSnippetsStore();
    const snippets: any = snippetsStore.snippets as unknown as any;
    const breadcrumbPrefix = computed(() => getBreadcrumbPrefix());

    interface IBreadcrumb {
        name: string;
        url: string;
    }

    const injectedPath = inject('breadcrumbs') as Ref<IBreadcrumb[]> | IBreadcrumb[] | undefined;

    const route = useRoute();
    const router = useRouter();
    const seoMap = useState<Record<string, string>>('categorySeoMap', () => ({}));
    const navigationSource = useState<any>('plp-navigation-source', () => null);

    const breadcrumbs = ref<IBreadcrumb[]>([]);

    /** Avoid SSR/client mismatch: `getBreadcrumbPrefix()` is client-only (session history). */
    const canRenderBreadcrumbPrefix = ref(false);
    onMounted(() => {
        canRenderBreadcrumbPrefix.value = true;
    });

    const updateBreadcrumbs = () => {
        const path = unref(injectedPath);
        const base = Array.isArray(path) && path.length > 0 ? path : generateBreadcrumbs(route.fullPath);
        breadcrumbs.value = base.map((b) =>
            b.name?.toLowerCase() === 'home' ? { ...b, url: '/nl/' } : b,
        );
    };

    // Initial update
    updateBreadcrumbs();

    // Watch for route changes
    watch(() => route.fullPath, updateBreadcrumbs);

    // Watch for injected path changes (if it's a ref)
    if (isRef(injectedPath)) {
        watch(injectedPath, updateBreadcrumbs);
    }

    function capitalize(word: string): string {
        return word ? word.charAt(0).toUpperCase() + word?.slice(1) : word;
    }

    function breadcrumbTarget(url: string) {
        const target = getPath(url);
        const resolved = router.resolve(target);
        const normalize = (path: string) => '/' + path.replace(/^\/+|\/+$/g, '');
        const path = normalize(resolved.path);
        // Home and brand overviews never inherit filters, even with a category ID in the SEO map.
        if (path === '/' || path === '/nl' || /^\/(?:nl\/)?(?:(?:dames|heren)\/)?merken$/.test(path)) return resolved.path;
        if (!props.retainFilters) return target;

        const source = navigationSource.value?.path === route.path ? navigationSource.value : null;
        const isCategory = Object.values(seoMap.value).some(url => normalize(url) === path);
        const isBrandCategory = !!source?.brandRoutes?.[path];
        if (!isCategory && !isBrandCategory && path !== '/search') return target;

        return {
            path: resolved.path,
            query: categoryNavigationQuery(
                route.query,
                path === '/search' ? { tn_q: route.query.tn_q, ...resolved.query } : resolved.query,
                departmentFromPath(route.path) || source?.department,
                departmentFromPath(resolved.path),
            ),
            hash: resolved.hash,
        };
    }

    function handleClick(event: MouseEvent, targetPath: string) {
        // Filter-aware links must use their resolved destination, not an older history entry.
        if (props.retainFilters) return;
        event.preventDefault();
        handleBreadCrumbPrefixClick(targetPath);
    }

    function formatHyphenated(segment: string): string {
        // Keep '-' and capitalize each hyphen-separated part
        return segment
            .split('-')
            .filter(Boolean)
            .map(part => capitalize(part))
            .join('-');
    }

    function getPath(url: string): string {
        if (!/^https?:\/\//i.test(url)) {
            return url;
        }

        const urlObject = new URL(url);
        return urlObject.pathname + urlObject.search + urlObject.hash;
    }

    function generateBreadcrumbs(path: string): IBreadcrumb[] {
        const breadcrumbs = [];
        breadcrumbs.push({ name: 'Home', url: '/nl/' });
        const cleanPath = path.split('?')[0];
        const segments = cleanPath.split('/').filter(segment => segment !== '');

        segments.forEach((segment, index) => {
            if (segment === 'nl') {
                return;
            }

            if (segment === 'look') {
                let url = '/nl/inspiratie/';
                const pageDataStore = usePageDataStore() as any;
                if (pageDataStore?.data?.context) {
                    if (pageDataStore?.data?.context === 'men') {
                        url = '/nl/heren/inspiratie/';
                    }

                    if (pageDataStore?.data?.context === 'ladies') {
                        url = '/nl/dames/inspiratie/';
                    }
                }

                breadcrumbs.push({
                    name: 'look',
                    url: url,
                });
                return;
            }

            breadcrumbs.push({
                name: formatHyphenated(segment),
                url: '/' + segments?.slice(0, index + 1).join('/') + '/',
            });
        });

        return breadcrumbs;
    }
</script>
